import { useCallback, useMemo, useState } from 'react';
import type { MemberOverlayName } from '@library/types/members';
import { useGetMemberQuery } from '@bo-services/members/membersApi';
import { useMembersFeatureFlags } from '@bo-hooks/members';
import { useParams } from 'react-router-dom';

export const useMemberDetails = () => {
  const { memberId } = useParams();
  const [displayOverlayName, setDisplayOverlayName] =
    useState<MemberOverlayName>(undefined);

  const featureFlags = useMembersFeatureFlags();

  // Fetch member details
  const {
    data: memberProfile,
    isError,
    isFetching,
    isLoading,
    isSuccess
  } = useGetMemberQuery(
    { id: memberId ?? '', featureFlags },
    { skip: !memberId }
  );

  const handleCloseOverlay = useCallback(() => {
    setDisplayOverlayName(undefined);
  }, []);

  const handleShowOverlay = useCallback((value: MemberOverlayName) => {
    setDisplayOverlayName(value);
  }, []);

  const overlayActive = useMemo(() => {
    return {
      changeLogDrawerActive: displayOverlayName === 'CHANGE_LOG',
      createAdjustmentModalActive: displayOverlayName === 'CREATE_ADJUSTMENT',
      createNoteModalActive: displayOverlayName === 'CREATE_NOTE',
      editAccountInformationModalActive:
        displayOverlayName === 'EDIT_ACCOUNT_INFORMATION',
      enrollKindbridgeModalActive: displayOverlayName === 'ENROLL_KINDBRIDGE',
      manageLegacyRulesetModalActive:
        displayOverlayName === 'MANAGE_LEGACY_RULESET',
      manageMemberGroupsModalActive:
        displayOverlayName === 'MANAGE_MEMBER_GROUPS',
      manageTinCollectionModalActive:
        displayOverlayName === 'MANAGE_TIN_COLLECTION',
      manageVipStatusModalActive: displayOverlayName === 'MANAGE_VIP_STATUS',
      manuallyPassMemberModalActive:
        displayOverlayName === 'MANUALLY_PASS_MEMBER',
      resetPasswordModalActive: displayOverlayName === 'RESET_PASSWORD',
      updateMemberActivationModalActive:
        displayOverlayName === 'UPDATE_MEMBER_ACTIVATION'
    };
  }, [displayOverlayName]);

  return {
    isError,
    isLoading: isFetching || isLoading,
    isSuccess,
    memberId,
    memberProfile,
    onCloseOverlay: handleCloseOverlay,
    onShowOverlay: handleShowOverlay,
    overlayActive
  };
};
